import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  ScrollArea,
} from '@/components/ui/scroll-area';
import { formatCellValue } from '@/lib/utils';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  Bug,
  RefreshCw,
  Search,
  Download,
  Play,
  Pause,
  Terminal,
  Clock,
  User,
  Settings,
  Trash2,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Log level colors and icons
const LEVEL_CONFIG = {
  DEBUG: { color: 'text-gray-500', bg: 'bg-gray-100', icon: Bug },
  INFO: { color: 'text-blue-600', bg: 'bg-blue-100', icon: Info },
  WARNING: { color: 'text-amber-600', bg: 'bg-amber-100', icon: AlertTriangle },
  ERROR: { color: 'text-red-600', bg: 'bg-red-100', icon: AlertCircle },
  CRITICAL: { color: 'text-red-800', bg: 'bg-red-200', icon: AlertCircle },
};

const LevelBadge = ({ level }) => {
  const config = LEVEL_CONFIG[level] || LEVEL_CONFIG.INFO;
  const Icon = config.icon;
  return (
    <Badge variant="outline" className={`${config.bg} ${config.color} border-0 font-mono text-xs`}>
      <Icon className="h-3 w-3 mr-1" />
      {level}
    </Badge>
  );
};

export default function ApplicationLogsPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeTab, setActiveTab] = useState('logs');
  
  // Filters
  const [filters, setFilters] = useState({
    level: '',
    service: '',
    tenant_id: '',
    search: '',
    logger_name: '',
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 100,
    offset: 0,
  });
  
  // WebSocket ref
  const wsRef = useRef(null);
  const logsContainerRef = useRef(null);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: pagination.offset.toString(),
      });
      
      if (filters.level) params.append('level', filters.level);
      if (filters.service) params.append('service', filters.service);
      if (filters.tenant_id) params.append('tenant_id', filters.tenant_id);
      if (filters.search) params.append('search', filters.search);
      if (filters.logger_name) params.append('logger_name', filters.logger_name);
      
      const response = await fetch(`${API_URL}/api/superadmin/logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setPagination(prev => ({ ...prev, total: data.total }));
      } else {
        toast.error('Failed to fetch logs');
      }
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      toast.error('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [token, filters, pagination.limit, pagination.offset]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/superadmin/logs/stats?hours=24`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      } else {
        toast.error('Failed to fetch log stats');
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch config
  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/superadmin/logs/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setConfig(await response.json());
      } else {
        toast.error('Failed to fetch log config');
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchConfig();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only; filter changes handled by separate effect
  }, []);

  // Refetch when filters/pagination change
  useEffect(() => {
    if (!loading) {
      fetchLogs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loading is a guard to prevent concurrent fetches
  }, [filters, pagination.offset, fetchLogs]);

  // WebSocket streaming
  const startStreaming = () => {
    if (wsRef.current) return;
    
    const wsUrl = `${API_URL.replace('http', 'ws')}/api/superadmin/logs/stream`;
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      // Send auth token
      ws.send(JSON.stringify({ token }));
      setStreaming(true);
      toast.success('Connected to log stream');
    };
    
    ws.onmessage = (event) => {
      try {
        // Handle raw ping/pong frames from proxies
        if (event.data === 'pong' || event.data === 'ping') {
          return;
        }
        
        const data = JSON.parse(event.data);
        
        if (data.type === 'history') {
          // Initial history
          setLogs(prev => [...data.logs, ...prev].slice(0, 500));
        } else if (data.type === 'pong') {
          // Heartbeat response
        } else {
          // New log entry
          setLogs(prev => [data, ...prev].slice(0, 500));
          
          // Auto-scroll if at top
          if (logsContainerRef.current?.scrollTop === 0) {
            logsContainerRef.current?.scrollTo({ top: 0 });
          }
        }
      } catch (err) {
        console.error('WebSocket message error:', err);
      }
    };
    
    ws.onclose = () => {
      setStreaming(false);
      wsRef.current = null;
    };
    
    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      toast.error('Stream connection error');
      setStreaming(false);
    };
    
    wsRef.current = ws;
  };

  const stopStreaming = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setStreaming(false);
      toast.info('Disconnected from log stream');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Export logs
  const handleExport = async (format = 'json') => {
    try {
      const params = new URLSearchParams({ format });
      if (filters.level) params.append('level', filters.level);
      if (filters.search) params.append('search', filters.search);
      
      const response = await fetch(`${API_URL}/api/superadmin/logs/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_export.${format}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Logs exported');
      }
    } catch (_err) {
      toast.error('Export failed');
    }
  };

  // Cleanup old logs
  const handleCleanup = async () => {
    if (!confirm(`Delete logs older than ${config?.config?.retention_days || 30} days?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/superadmin/logs/cleanup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Old logs cleaned up');
        fetchLogs();
        fetchStats();
      }
    } catch (_err) {
      toast.error('Cleanup failed');
    }
  };

  // Update config
  const handleUpdateConfig = async (updates) => {
    try {
      const response = await fetch(`${API_URL}/api/superadmin/logs/config`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig({ config: data.config });
        toast.success('Configuration updated');
      }
    } catch (_err) {
      toast.error('Failed to update config');
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return ts;
    }
  };

  const truncateMessage = (msg, maxLen = 100) => {
    if (!msg) return '';
    return msg.length > maxLen ? msg.substring(0, maxLen) + '...' : msg;
  };

  if (loading) {
    return (
      <PageSkeleton maxWidth="7xl" kpis={5} sections={2} data-testid="application-logs-loading" />
    );
  }

  return (
    <PageShell maxWidth="7xl" data-testid="application-logs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Terminal className="h-6 w-6" />
            Application Logs
          </h1>
          <p className="text-muted-foreground">
            Monitor and debug application logs in real-time
          </p>
        </div>
        <div className="flex items-center gap-2">
          {streaming ? (
            <Button variant="destructive" size="sm" onClick={stopStreaming}>
              <Pause className="h-4 w-4 mr-2" />
              Stop Stream
            </Button>
          ) : (
            <Button variant="default" size="sm" onClick={startStreaming}>
              <Play className="h-4 w-4 mr-2" />
              Live Stream
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => fetchLogs()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('json')}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-5">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total (24h)</span>
              </div>
              <p className="text-2xl font-bold">{stats.total_logs?.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-muted-foreground">Errors</span>
              </div>
              <p className="text-2xl font-bold text-red-600">
                {(stats.by_level?.ERROR || 0) + (stats.by_level?.CRITICAL || 0)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span className="text-sm text-muted-foreground">Warnings</span>
              </div>
              <p className="text-2xl font-bold text-amber-600">
                {stats.by_level?.WARNING || 0}
              </p>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-blue-500" />
                <span className="text-sm text-muted-foreground">Info</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {stats.by_level?.INFO || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Retention</span>
              </div>
              <p className="text-2xl font-bold">{config?.config?.retention_days || 30}d</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="logs">Log Viewer</TabsTrigger>
          <TabsTrigger value="errors">Recent Errors</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        {/* Log Viewer Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid gap-4 md:grid-cols-5">
                <div>
                  <Label className="text-xs">Level</Label>
                  <Select
                    value={filters.level}
                    onValueChange={(v) => setFilters({ ...filters, level: v === "all" ? "" : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="DEBUG">DEBUG+</SelectItem>
                      <SelectItem value="INFO">INFO+</SelectItem>
                      <SelectItem value="WARNING">WARNING+</SelectItem>
                      <SelectItem value="ERROR">ERROR+</SelectItem>
                      <SelectItem value="CRITICAL">CRITICAL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Service</Label>
                  <Select
                    value={filters.service}
                    onValueChange={(v) => setFilters({ ...filters, service: v === "all" ? "" : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Services" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      <SelectItem value="backend">Backend</SelectItem>
                      <SelectItem value="eva_agent">Eva Agent</SelectItem>
                      <SelectItem value="scheduler">Scheduler</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Logger</Label>
                  <Input
                    placeholder="e.g., routers.auth"
                    value={filters.logger_name}
                    onChange={(e) => setFilters({ ...filters, logger_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Tenant ID</Label>
                  <Input
                    placeholder="Filter by tenant"
                    value={filters.tenant_id}
                    onChange={(e) => setFilters({ ...filters, tenant_id: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      placeholder="Search message..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {logs.length} of {pagination.total.toLocaleString()} logs
                  {streaming && <Badge variant="outline" className="ml-2 bg-green-100 text-green-700">LIVE</Badge>}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.offset === 0}
                    onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Log Table */}
          <Card>
            <ScrollArea className="h-[500px]" ref={logsContainerRef}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[140px]">Timestamp</TableHead>
                    <TableHead className="w-[90px]">Level</TableHead>
                    <TableHead className="w-[180px]">Logger</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="w-[100px]">Tenant</TableHead>
                    <TableHead className="w-[80px]">ReqID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No logs found matching filters
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow
                        key={log.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setSelectedLog(log)}
                      >
                        <TableCell className="font-mono text-xs">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <LevelBadge level={log.level} />
                        </TableCell>
                        <TableCell className="font-mono text-xs truncate max-w-[180px]" title={log.logger_name}>
                          {formatCellValue(log.logger_name)}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          <span className={log.stack_trace ? 'text-red-600' : ''}>
                            {formatCellValue(truncateMessage(log.message, 80))}
                          </span>
                          {log.stack_trace && (
                            <Badge variant="outline" className="ml-2 text-[10px]">TRACE</Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs truncate max-w-[100px]" title={log.tenant_id}>
                          {formatCellValue(log.tenant_id?.split('-')[0])}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {formatCellValue(log.request_id)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </Card>
        </TabsContent>

        {/* Recent Errors Tab */}
        <TabsContent value="errors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                Recent Errors (24h)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.recent_errors?.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">
                  No errors in the last 24 hours 🎉
                </p>
              ) : (
                <div className="space-y-3">
                  {stats?.recent_errors?.map((error, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 cursor-pointer hover:border-red-300"
                      onClick={() => {
                        // Find and show full log
                        const fullLog = logs.find(l => l.id === error.id);
                        if (fullLog) setSelectedLog(fullLog);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-mono text-xs text-red-800 dark:text-red-200">
                            {error.logger_name}
                          </p>
                          <p className="text-sm mt-1">{truncateMessage(error.message, 200)}</p>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTimestamp(error.timestamp)}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        {error.tenant_id && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {error.tenant_id.split('-')[0]}
                          </span>
                        )}
                        {error.request_id && (
                          <span className="font-mono">#{error.request_id}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuration Tab */}
        <TabsContent value="config" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Logging Configuration
              </CardTitle>
              <CardDescription>
                Configure log retention and minimum log level
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Retention Period (days)</Label>
                  <Select
                    value={config?.config?.retention_days?.toString() || '30'}
                    onValueChange={(v) => handleUpdateConfig({ retention_days: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Logs older than this will be automatically deleted
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Minimum Log Level</Label>
                  <Select
                    value={config?.config?.min_level || 'WARNING'}
                    onValueChange={(v) => handleUpdateConfig({ min_level: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DEBUG">DEBUG (all logs)</SelectItem>
                      <SelectItem value="INFO">INFO</SelectItem>
                      <SelectItem value="WARNING">WARNING (recommended)</SelectItem>
                      <SelectItem value="ERROR">ERROR only</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Only logs at this level or higher will be stored
                  </p>
                </div>
              </div>
              
              <div className="border-t pt-6">
                <h4 className="font-medium mb-4">Maintenance</h4>
                <div className="flex items-center gap-4">
                  <Button variant="outline" onClick={handleCleanup}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clean Up Old Logs
                  </Button>
                  <p className="text-sm text-muted-foreground">
                    Manually delete logs older than {config?.config?.retention_days || 30} days
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LevelBadge level={selectedLog?.level} />
              Log Details
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {selectedLog?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4">
                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">Timestamp</Label>
                    <p className="font-mono">{formatTimestamp(selectedLog.timestamp)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Logger</Label>
                    <p className="font-mono">{selectedLog.logger_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Service</Label>
                    <p>{selectedLog.service}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Environment</Label>
                    <p>{selectedLog.environment}</p>
                  </div>
                  {selectedLog.tenant_id && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Tenant ID</Label>
                      <p className="font-mono text-xs">{selectedLog.tenant_id}</p>
                    </div>
                  )}
                  {selectedLog.user_email && (
                    <div>
                      <Label className="text-xs text-muted-foreground">User</Label>
                      <p>{selectedLog.user_email}</p>
                    </div>
                  )}
                  {selectedLog.request_id && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Request ID</Label>
                      <p className="font-mono">{selectedLog.request_id}</p>
                    </div>
                  )}
                  {selectedLog.request_path && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Request</Label>
                      <p className="font-mono text-xs">{selectedLog.request_method} {selectedLog.request_path}</p>
                    </div>
                  )}
                </div>

                {/* Message */}
                <div>
                  <Label className="text-xs text-muted-foreground">Message</Label>
                  <pre className="mt-1 p-3 rounded bg-muted font-mono text-xs whitespace-pre-wrap break-all">
                    {selectedLog.message}
                  </pre>
                </div>

                {/* Stack Trace */}
                {selectedLog.stack_trace && (
                  <div>
                    <Label className="text-xs text-muted-foreground flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      Stack Trace
                    </Label>
                    <pre className="mt-1 p-3 rounded bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200 font-mono text-xs whitespace-pre-wrap overflow-x-auto">
                      {selectedLog.stack_trace}
                    </pre>
                  </div>
                )}

                {/* Extra Data */}
                {selectedLog.extra_data && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Extra Data</Label>
                    <pre className="mt-1 p-3 rounded bg-muted font-mono text-xs whitespace-pre-wrap">
                      {JSON.stringify(JSON.parse(selectedLog.extra_data), null, 2)}
                    </pre>
                  </div>
                )}

                {/* Source Location */}
                {selectedLog.filename && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Source</Label>
                    <p className="font-mono text-xs">
                      {selectedLog.filename}:{selectedLog.line_no} in {selectedLog.func_name}()
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
